import { api } from './api'
import { throwApiError } from './apiHelpers'

function assertSuccess(body: { success?: boolean; message?: string; data?: unknown }) {
  if (!body?.success) {
    const err = new Error(body?.message || 'Request failed') as Error & { code?: string }
    err.code = (body as { error?: { code?: string } }).error?.code
    throw err
  }
  return body.data
}

export type StoreSubscriptionAdminSnapshot = {
  storeId: number
  storeSubscriptionId: number | null
  source: 'store'
  subscription: {
    planCode: string
    status: string
    billingInterval: 'monthly' | 'yearly'
    trialEndsAt: string | null
    currentPeriodEndsAt: string | null
    graceEndsAt: string | null
    billingRequired: boolean
    complimentary?: boolean
    eligibleTrial: boolean
    planName: string | null
    planAmount: number | null
    currency: string
    displayPrice: string | null
    maxStaffCount: number | null
    staffCount: number
    staffSeatsRemaining: number | null
    billingCurrency?: string | null
    billingProvider?: string | null
    billingLockedAt?: string | null
  }
}

export type StoreRenewalOutcome = 'charged' | 'expired' | 'skipped'

/** super_admin — per-store SaaS subscription snapshot */
export async function getStoreSubscription(storeId: number | string): Promise<StoreSubscriptionAdminSnapshot> {
  try {
    const { data } = await api.get(`/admin/stores/${encodeURIComponent(String(storeId))}/subscription`)
    return assertSuccess(data) as StoreSubscriptionAdminSnapshot
  } catch (e) {
    throwApiError(e, 'Failed to load store subscription')
  }
}

/** super_admin — set complimentary access end date (FREE stores). `null` = unlimited. */
export async function patchStoreComplimentaryAccess(
  storeId: number | string,
  trialEndsAt: string | null,
): Promise<StoreSubscriptionAdminSnapshot> {
  try {
    const { data } = await api.patch(
      `/admin/stores/${encodeURIComponent(String(storeId))}/subscription`,
      { trialEndsAt },
    )
    return assertSuccess(data) as StoreSubscriptionAdminSnapshot
  } catch (e) {
    throwApiError(e, 'Failed to update complimentary access')
  }
}

/** super_admin — trigger Mollie renewal charge (ignores due-date guard) */
export async function renewStoreSubscription(storeId: number | string): Promise<{
  outcome: StoreRenewalOutcome
  storeId: number
  storeSubscriptionId: number
  subscription: StoreSubscriptionAdminSnapshot['subscription']
}> {
  try {
    const { data } = await api.post(
      `/admin/stores/${encodeURIComponent(String(storeId))}/subscription/renew`,
    )
    return assertSuccess(data) as {
      outcome: StoreRenewalOutcome
      storeId: number
      storeSubscriptionId: number
      subscription: StoreSubscriptionAdminSnapshot['subscription']
    }
  } catch (e) {
    throwApiError(e, 'Failed to trigger renewal')
  }
}
